import { Editor } from '@tiptap/core';
import type { Node as ProseMirrorNode } from '@tiptap/pm/model';

const LIST_ITEM_NODE_NAMES: ReadonlySet<string> = new Set(['listItem', 'taskItem']);

export interface EmptyListItemExit {
  itemPos: number;
  itemNode: ProseMirrorNode;
}

/**
 * Detects the Word/Google Docs "Enter on empty item" case: cursor collapsed
 * in an empty paragraph that is the sole content of its listItem/taskItem
 * (no nested sub-list hanging off it, which would need a normal split
 * instead). Pure function over the doc so it can be unit tested without a
 * full Editor instance.
 */
export function findEmptyListItemExit(doc: ProseMirrorNode, from: number, to: number): EmptyListItemExit | null {
  const $from = doc.resolve(from);
  const $to = doc.resolve(to);

  if (!$from.sameParent($to) || $from.parent.content.size > 0) {
    return null;
  }
  if ($from.depth < 2) {
    return null;
  }

  const itemNode = $from.node(-1);
  if (!LIST_ITEM_NODE_NAMES.has(itemNode.type.name) || itemNode.childCount !== 1) {
    return null;
  }

  return { itemPos: $from.before(-1), itemNode };
}

/**
 * Ending a list per tasks/edit-bullets.md: pressing Enter on an empty
 * bullet/numbered/task item removes that item and drops the cursor into a
 * normal paragraph (or one nesting level up, for a nested item) instead of
 * creating another empty item. Returns false when the cursor isn't on a
 * qualifying empty item so callers fall back to the normal
 * continue-the-list Enter handling.
 */
export function exitEmptyListItemOnEnter(editor: Editor): boolean {
  const { from, to } = editor.state.selection;
  const found = findEmptyListItemExit(editor.state.doc, from, to);
  if (!found) {
    return false;
  }

  return editor.chain().focus().liftListItem(found.itemNode.type.name).run();
}
